import FilterListIcon from "@mui/icons-material/FilterList";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  MenuItem,
  Pagination,
  Skeleton,
  TextField,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllPools, getLeagueTypes, startWeeklyLeaderboards, finalizeWeeklyLeaderboards } from "services/weeklyLeaderboardService";
import PoolCard from "./components/PoolCard";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
  },
  filterSection: {
    marginBottom: "32px",
    padding: "24px",
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e0e0e0",
  },
  filterTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: theme.palette.primary.main,
  },
  filterGrid: {
    marginBottom: "16px",
  },
  searchButton: {
    minHeight: "56px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "1rem",
  },
  refreshButton: {
    minHeight: "56px",
    borderRadius: "8px",
    fontWeight: 600,
  },
  statsCard: {
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    marginBottom: "24px",
  },
  statItem: {
    textAlign: "center",
    padding: "12px",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: "4px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "#fff",
    borderRadius: "16px",
    border: "2px dashed #e0e0e0",
  },
  emptyIcon: {
    fontSize: "80px",
    color: "#ccc",
    marginBottom: "16px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "32px",
    marginBottom: "16px",
  },
  poolsGrid: {
    marginTop: "24px",
  },
  actionButtonsContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
  },
  startButton: {
    minHeight: "48px",
    fontWeight: 600,
    fontSize: "1rem",
  },
  finalizeButton: {
    minHeight: "48px",
    fontWeight: 600,
    fontSize: "1rem",
  },
}));

const WeeklyLeaderboard = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedUserRef = useRef(null);

  const [leagueTypes, setLeagueTypes] = useState([]);
  const [filterBy, setFilterBy] = useState(""); // "", "leagueType", "poolId", "userId"
  const [selectedLeagueType, setSelectedLeagueType] = useState("");
  const [poolId, setPoolId] = useState("");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({
    title: "",
    message: "",
    btnText: "",
    onConfirm: null,
  });

  // Initialize from URL parameters
  useEffect(() => {
    const urlLeagueType = searchParams.get("leagueType") || "";
    const urlPoolId = searchParams.get("poolId") || "";
    const urlUserId = searchParams.get("userId") || "";
    const urlPage = parseInt(searchParams.get("page")) || 1;

    // Determine filter type from URL
    if (urlLeagueType) {
      setFilterBy("leagueType");
      setSelectedLeagueType(urlLeagueType);
    } else if (urlPoolId) {
      setFilterBy("poolId");
      setPoolId(urlPoolId);
    } else if (urlUserId) {
      setFilterBy("userId");
      setUserId(urlUserId);
    }

    setPage(urlPage);

    // Always mark as searched (filters are optional now)
    setHasSearched(true);
  }, []);

  // Fetch league types on mount
  useEffect(() => {
    fetchLeagueTypes();
  }, []);

  // Auto-fetch data when filters are restored from URL or on initial load
  useEffect(() => {
    if (!initialLoading && hasSearched) {
      fetchPools(false);
    }
  }, [initialLoading, hasSearched]);

  const fetchLeagueTypes = async () => {
    try {
      const response = await getLeagueTypes();
      if (response.data && Array.isArray(response.data)) {
        setLeagueTypes(response.data);
        // Don't set default league type - make it optional
      }
    } catch (error) {
      showSnackbar(
        error.message || "Failed to load league types",
        "error"
      );
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchPools = async (resetPage = false) => {
    // Validate filter selection if a filter type is chosen
    if (filterBy) {
      let filterValue = "";
      if (filterBy === "leagueType") filterValue = selectedLeagueType;
      if (filterBy === "poolId") filterValue = poolId;
      if (filterBy === "userId") filterValue = userId;

      if (!filterValue) {
        showSnackbar(`Please provide a value for ${filterBy}`, "warning");
        return;
      }
    }

    setLoading(true);
    const currentPage = resetPage ? 1 : page;
    if (resetPage) setPage(1);

    // Update URL parameters
    const params = new URLSearchParams();
    if (filterBy === "leagueType" && selectedLeagueType) {
      params.set("leagueType", selectedLeagueType);
    } else if (filterBy === "poolId" && poolId) {
      params.set("poolId", poolId);
    } else if (filterBy === "userId" && userId) {
      params.set("userId", userId);
    }
    params.set("page", currentPage.toString());
    setSearchParams(params);

    try {
      const response = await getAllPools({
        limit: 100,
        leagueType: filterBy === "leagueType" ? selectedLeagueType : null,
        page: currentPage,
        poolId: filterBy === "poolId" ? poolId : null,
        userId: filterBy === "userId" ? userId : null,
      });

      if (response.data) {
        setLeaderboardData(response.data);
        setHasSearched(true);

        // Scroll to highlighted user if searching by userId
        if (filterBy === "userId" && userId) {
          // Wait for DOM to update, then scroll
          setTimeout(() => {
            if (highlightedUserRef.current) {
              highlightedUserRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 300);
        }
      }
    } catch (error) {
      showSnackbar(
        error.message || "Failed to load leaderboard data",
        "error"
      );
      setLeaderboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchPools(true);
  };

  const handleRefresh = () => {
    fetchPools(false);
  };

  const handlePageChange = (_event, value) => {
    setPage(value);
    // Auto-fetch when page changes
    setTimeout(() => {
      fetchPools(false);
    }, 100);
  };

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleFilterByChange = (value: any) => {
    setFilterBy(value);
    // Clear all filter values when changing filter type
    setSelectedLeagueType("");
    setPoolId("");
    setUserId("");
  };

  const handleStartLeaderboards = () => {
    setConfirmDialogConfig({
      title: "Start New Leaderboards",
      message: "Are you sure you want to start new weekly leaderboards?",
      btnText: "Start",
      onConfirm: async () => {
        setConfirmDialogOpen(false);
        setActionLoading(true);
        try {
          const response = await startWeeklyLeaderboards();
          if (response.status) {
            showSnackbar(response.message || "New weekly leaderboards started successfully", "success");
          } else {
            showSnackbar(response.message || "Failed to start weekly leaderboards", "error");
          }
        } catch (error) {
          showSnackbar(
            error.message || error.details || "Error starting weekly leaderboards",
            "error"
          );
        } finally {
          setActionLoading(false);
        }
      },
    });
    setConfirmDialogOpen(true);
  };

  const handleFinalizeLeaderboards = () => {
    setConfirmDialogConfig({
      title: "Finalize Leaderboards",
      message: "Are you sure you want to finalize weekly leaderboards?",
      btnText: "Finalize",
      onConfirm: async () => {
        setConfirmDialogOpen(false);
        setActionLoading(true);
        try {
          const response = await finalizeWeeklyLeaderboards();
          if (response.status) {
            showSnackbar(response.message || "Weekly leaderboards finalized successfully", "success");
          } else {
            showSnackbar(response.message || "Failed to finalize weekly leaderboards", "error");
          }
        } catch (error) {
          showSnackbar(
            error.message || "Error finalizing weekly leaderboards",
            "error"
          );
        } finally {
          setActionLoading(false);
        }
      },
    });
    setConfirmDialogOpen(true);
  };

  if (initialLoading) {
    return (
      <ContentShell title="Weekly Leaderboard" loading={true}>
        <Box className={styles.loadingContainer}>
          <CircularProgress size={64} />
        </Box>
      </ContentShell>
    );
  }

  const allPools =
    leaderboardData?.leagues?.flatMap((league) => league.pools || []) || [];

  return (
    <ContentShell
      title="Weekly Leaderboard"
    >
      <Box className={styles.container}>
        {/* Action Buttons */}
        <Box className={styles.actionButtonsContainer}>
          <Button
            variant="contained"
            color="success"
            onClick={handleStartLeaderboards}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            className={styles.startButton}
          >
            {actionLoading ? "Processing..." : "Start New Leaderboards"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinalizeLeaderboards}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            className={styles.finalizeButton}
          >
            {actionLoading ? "Processing..." : "Finalize Leaderboards"}
          </Button>
        </Box>
        {/* Filter Section */}
        <Card className={styles.filterSection}>
          <Typography className={styles.filterTitle}>
            <FilterListIcon />
            Filters
          </Typography>

          <Grid container spacing={3} className={styles.filterGrid}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Filter By"
                value={filterBy}
                onChange={(e) => handleFilterByChange(e.target.value)}
                variant="outlined"
              >
                <MenuItem value="">
                  <em>No Filter</em>
                </MenuItem>
                <MenuItem value="leagueType">League Type</MenuItem>
                <MenuItem value="poolId">Pool ID</MenuItem>
                <MenuItem value="userId">User ID</MenuItem>
              </TextField>
            </Grid>

            {filterBy === "leagueType" && (
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="League Type"
                  value={selectedLeagueType}
                  onChange={(e) => setSelectedLeagueType(e.target.value)}
                  variant="outlined"
                >
                  <MenuItem value="">
                    <em>Select League Type</em>
                  </MenuItem>
                  {leagueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {filterBy === "poolId" && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Pool ID"
                  value={poolId}
                  onChange={(e) => setPoolId(e.target.value)}
                  onKeyDown={handleKeyPress}
                  variant="outlined"
                  placeholder="Enter pool ID"
                />
              </Grid>
            )}

            {filterBy === "userId" && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  onKeyDown={handleKeyPress}
                  variant="outlined"
                  placeholder="Enter user ID"
                />
              </Grid>
            )}

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                className={styles.searchButton}
              >
                {loading ? "Loading..." : "Search"}
              </Button>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleRefresh}
                disabled={!hasSearched || loading}
                startIcon={<RefreshIcon />}
                className={styles.refreshButton}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          {/* Info Alert */}
          <Alert severity="info" sx={{ marginTop: 2 }}>
            <strong>Note:</strong> Filters are optional. Select a filter type from "Filter By" to narrow down results. Without filters, all pools will be displayed.
          </Alert>
        </Card>

        {/* Stats Card */}
        {hasSearched && leaderboardData && (
          <Card className={styles.statsCard}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box className={styles.statItem}>
                  <Typography className={styles.statValue}>
                    {leaderboardData.totalPools || 0}
                  </Typography>
                  <Typography className={styles.statLabel}>
                    Total Pools
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box className={styles.statItem}>
                  <Typography className={styles.statValue}>
                    {leaderboardData.totalUsers || 0}
                  </Typography>
                  <Typography className={styles.statLabel}>
                    Total Users
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box className={styles.statItem}>
                  <Typography className={styles.statValue}>
                    {filterBy === "leagueType" && selectedLeagueType
                      ? selectedLeagueType.replace(/_/g, " ")
                      : filterBy === "poolId" && poolId
                      ? `Pool: ${poolId}`
                      : filterBy === "userId" && userId
                      ? `User: ${userId}`
                      : "All Pools"}
                  </Typography>
                  <Typography className={styles.statLabel}>
                    {filterBy === "leagueType"
                      ? "League Type"
                      : filterBy === "poolId"
                      ? "Pool Filter"
                      : filterBy === "userId"
                      ? "User Filter"
                      : "No Filter"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* Pagination */}
        {hasSearched && !loading && leaderboardData && leaderboardData.totalPools > 0 && (
          <Box className={styles.paginationContainer}>
            <Pagination
              count={Math.ceil(leaderboardData.totalPools / 100)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Loading State */}
        {loading && hasSearched && (
          <Grid container spacing={3} className={styles.poolsGrid}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} lg={6} key={i}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty State - No Search Yet */}
        {!hasSearched && !loading && (
          <Box className={styles.emptyState}>
            <LeaderboardIcon className={styles.emptyIcon} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Loading Leaderboard Data
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Please wait while we load the leaderboard pools...
            </Typography>
          </Box>
        )}

        {/* Empty State - No Results */}
        {hasSearched && !loading && allPools.length === 0 && (
          <Box className={styles.emptyState}>
            <LeaderboardIcon className={styles.emptyIcon} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              No Pools Found
            </Typography>
            <Typography variant="body1" color="textSecondary">
              No pools match your current filters. Try adjusting your search criteria.
            </Typography>
          </Box>
        )}

        {/* Pools Grid */}
        {hasSearched && !loading && allPools.length > 0 && (
          <Grid container spacing={3} className={styles.poolsGrid}>
            {allPools.map((pool) => (
              <Grid item xs={12} lg={6} key={pool.poolId}>
                <PoolCard
                  pool={pool}
                  highlightedUserId={filterBy === "userId" ? userId : null}
                  userRef={highlightedUserRef}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Confirmation Dialog */}
        <DeleteConsentDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={confirmDialogConfig.onConfirm}
          itemName=""
          btnText={confirmDialogConfig.btnText}
          message={confirmDialogConfig.message}
        />
      </Box>
    </ContentShell>
  );
};

export default WeeklyLeaderboard;
